import React, { useEffect } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Card, CardBody, Col, Container, Label, Row, Spinner } from "reactstrap";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { useTranslation } from "react-i18next";
import { userActions } from "../../store/auth/login/userSlice";
import { dataActions } from "../../modules/dataSlice";
import logo from "../../assets/images/ja-eval_logo_horizontal_transparent.png";
import authBg from "../../assets/images/authentication-bg.jpg";
import "./login-modern.css";

function Login() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation("translation");
    const { loading, error } = useSelector(state => state.user);
    const { isFetching } = useSelector(state => state.data);
    const isBusy = loading || isFetching;

    useEffect(() => {
        document.body.classList.add("auth-body-bg");
        return () => {
            document.body.classList.remove("auth-body-bg");
        };
    }, []);

    const formik = useFormik({
        initialValues: { username: "", password: "" },
        onSubmit: (values) => {
            dispatch(userActions.login({
                user: values,
                history: navigate,
                onSuccess: () => {
                    setTimeout(() => {
                        dispatch(dataActions.loadData());
                    }, 2000);
                },
            }));
        },
    });

    return (
        <React.Fragment>
            {isBusy && (
                <div id="preloader">
                    <div id="status">
                        <div className="spinner">
                            <i className="ri-loader-line spin-icon"></i>
                        </div>
                    </div>
                </div>
            )}

            <div className="modern-login-page">
                <Container fluid className="h-100 p-0">
                    <Row className="g-0 min-vh-100">
                        <Col lg={7} className="login-brand-panel d-none d-lg-flex">
                            <div className="login-visual" style={{ backgroundImage: `url(${authBg})` }}></div>
                            <div className="brand-content">
                                <div className="brand-badge">FMSAR Vehicle Intelligence</div>
                                <h1>Plateforme de gestion automobile pour opérations exigeantes.</h1>
                                <p>
                                    Accédez à vos dossiers véhicules, documents OCR, valeurs vénales et workflows
                                    de suivi dans un espace sécurisé conçu pour les équipes métier.
                                </p>
                                <div className="brand-stats">
                                    <div>
                                        <strong>Documents</strong>
                                        <span>Carte grise, CIN et facture centralisées</span>
                                    </div>
                                    <div>
                                        <strong>Valeur vénale</strong>
                                        <span>Suivi des estimations et historiques</span>
                                    </div>
                                    <div>
                                        <strong>OCR intelligent</strong>
                                        <span>Données extraites et vérifiables</span>
                                    </div>
                                </div>
                            </div>
                        </Col>

                        <Col lg={5} className="login-form-panel">
                            <div className="login-card-wrapper">
                                <Card className="modern-login-card">
                                    <CardBody>
                                        <div className="login-header">
                                            <img src={logo} alt="FMSAR" className="login-logo" />
                                            <span className="login-kicker">Espace entreprise</span>
                                            <h2>{t("login.welcome")}</h2>
                                            <p>{t("login.title")}</p>
                                        </div>

                                        {error && <Alert color="danger" className="modern-alert">{error}</Alert>}

                                        <AvForm className="form-horizontal login-form-content" onValidSubmit={formik.handleSubmit}>
                                            <div className="modern-field">
                                                <Label htmlFor="username">{t("login.username")}</Label>
                                                <div className="field-wrapper">
                                                    <i className="ri-user-3-line"></i>
                                                    <AvField
                                                        {...formik.getFieldProps("username")}
                                                        type="text"
                                                        id="username"
                                                        placeholder={t("login.username")}
                                                        validate={{ required: { value: true } }}
                                                        disabled={isBusy}
                                                    />
                                                </div>
                                            </div>

                                            <div className="modern-field">
                                                <Label htmlFor="userpassword">{t("login.password")}</Label>
                                                <div className="field-wrapper">
                                                    <i className="ri-lock-password-line"></i>
                                                    <AvField
                                                        {...formik.getFieldProps("password")}
                                                        type="password"
                                                        id="userpassword"
                                                        placeholder={t("login.password")}
                                                        validate={{ required: { value: true } }}
                                                        disabled={isBusy}
                                                    />
                                                </div>
                                            </div>

                                            <Button className="modern-login-btn" type="submit" disabled={isBusy}>
                                                {isBusy ? (
                                                    <React.Fragment>
                                                        <Spinner size="sm" className="me-2" />
                                                        Connexion...
                                                    </React.Fragment>
                                                ) : (
                                                    <React.Fragment>
                                                        {t("actions.login")}
                                                        <i className="ri-arrow-right-line ms-2"></i>
                                                    </React.Fragment>
                                                )}
                                            </Button>
                                        </AvForm>

                                        <div className="login-footer">
                                            <p>Développé par <a href="https://ideracloud.com" target="_blank" rel="noreferrer">IDERACLOUD</a> en partenariat avec YALILA GROUP</p>
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
}

export default Login;
